import { useReactiveVar } from '@apollo/client';
import Cancel from '@mui/icons-material/CancelOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { SnackNotification } from 'common/types/snack-notification';
import { SnackbarKey, useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { cacheActions, cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';

let displayed: SnackbarKey[] = [];

const SnackNotifier = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [redirect, setNavigate] = useState('');
  const notifications = useSelector((state: RootState) => cacheSelectors.snackNotifications(state));

  const storeDisplayed = (id: SnackbarKey) => {
    displayed = [...displayed, id];
  };

  const removeDisplayed = (id: SnackbarKey) => {
    displayed = [...displayed.filter(key => id !== key)];
  };

  React.useEffect(() => {
    const closeSnackNotification = (key: SnackbarKey | undefined) => {
      dispatch(
        cacheActions.addSnacks(
          notifications.map((x: SnackNotification) => (!key || key === x.key ? { ...x, dismissed: true } : x)),
        ),
      );
    };

    const removeSnackNotification = (key: SnackbarKey) => {
      dispatch(cacheActions.addSnacks(notifications.filter(x => x.key !== key)));
    };

    const closeAction = (key: SnackbarKey) => (
      <IconButton
        style={{ color: 'white' }}
        onClick={() => {
          closeSnackNotification(key);
        }}
        size="large"
      >
        <Cancel />
      </IconButton>
    );

    const linkAction = (key: SnackbarKey, actionText: string, actionLink: string) => (
      <>
        <Button
          style={{ color: 'white' }}
          onClick={() => {
            closeSnackNotification(key);
            setNavigate(actionLink);
          }}
        >
          {actionText}
        </Button>
        <IconButton
          style={{ color: 'white' }}
          onClick={() => {
            closeSnackNotification(key);
          }}
          size="large"
        >
          <Cancel />
        </IconButton>
      </>
    );

    notifications.forEach(
      ({ key, message, variant, persist, options = {}, actionText, actionLink, dismissed = false }) => {
        if (dismissed) {
          // dismiss snackbar using notistack
          closeSnackbar(key);
          return;
        }

        // do nothing if snackbar is already displayed
        if (key && displayed.includes(key)) return;

        // display snackbar using notistack
        enqueueSnackbar(message, {
          key,
          ...options,
          variant,
          persist,
          action: (key: SnackbarKey) =>
            actionText && actionLink ? linkAction(key, actionText, actionLink) : closeAction(key),
          onClose: (event, reason, myKey) => {
            if (options.onClose) {
              options.onClose(event, reason, myKey);
            }
          },
          onExited: (event, myKey) => {
            removeSnackNotification(myKey);
            removeDisplayed(myKey);
          },
        });

        // keep track of snackbars that we've displayed
        storeDisplayed(key as SnackbarKey);
      },
    );
  }, [notifications, closeSnackbar, enqueueSnackbar, dispatch]);

  if (redirect.length) return <Navigate to={redirect} />;
  return null;
};

export default SnackNotifier;
